/**
 * CompanySetupModal - Onboarding company profile setup
 * 
 * Beautiful, premium modal for completing company information during onboarding.
 * Reuses the existing tenant settings API endpoint (contractor_setup).
 * 
 * Design principles:
 * - Max width ~640px
 * - Generous padding (32px)
 * - Clear section spacing (24px)
 * - Readable labels and helper text
 * - Clean validation presentation
 * - Mobile-safe with sticky footer
 */

import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { Buildings, MapPin, Phone, SpinnerGap } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

export default function CompanySetupModal({ isOpen, onClose, onComplete, prefillData = {} }) {
  const [formData, setFormData] = useState({
    business_name: "",
    phone: "",
    address_street: "",
    address_city: "",
    address_state: "",
    address_zip: "",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Prefill form when modal opens or prefillData changes
  useEffect(() => {
    if (isOpen && prefillData) {
      setFormData({
        business_name: prefillData.company_name || "",
        phone: prefillData.phone || "",
        address_street: prefillData.address_street || "",
        address_city: prefillData.address_city || "",
        address_state: prefillData.address_state || "",
        address_zip: prefillData.address_zip || "",
      });
      setErrors({});
    }
  }, [isOpen, prefillData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.business_name.trim()) {
      newErrors.business_name = "Business name is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else {
      // Basic phone validation - at least 10 digits
      const digits = formData.phone.replace(/\D/g, "");
      if (digits.length < 10) {
        newErrors.phone = "Please enter a valid phone number";
      }
    }

    if (!formData.address_street.trim()) {
      newErrors.address_street = "Street address is required";
    }

    if (!formData.address_city.trim()) {
      newErrors.address_city = "City is required";
    }

    if (!formData.address_state.trim()) {
      newErrors.address_state = "State is required";
    }

    if (!formData.address_zip.trim()) {
      newErrors.address_zip = "ZIP code is required";
    } else {
      // Basic ZIP validation
      const cleanZip = formData.address_zip.replace(/[\s-]/g, "");
      if (!/^\d{5}(\d{4})?$/.test(cleanZip)) {
        newErrors.address_zip = "Enter a valid 5-digit ZIP";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please fix the errors above");
      return;
    }

    setSaving(true);
    try {
      // Step 1: Save company information using the standard tenant update endpoint
      // This endpoint works for all authenticated users (no role restrictions)
      const response = await fetch(`${API}/tenants/current`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          company_name: formData.business_name.trim(),
          phone: formData.phone.trim(),
          address_street: formData.address_street.trim(),
          address_city: formData.address_city.trim(),
          address_state: formData.address_state.trim().toUpperCase(),
          address_zip: formData.address_zip.trim(),
        }),
      });

      if (response.ok) {
        // Step 2: Mark onboarding company step as complete
        // This is a separate call to avoid coupling settings save with onboarding state
        await fetch(`${API}/onboarding/mark-company-completed`, {
          method: "POST",
          credentials: "include",
        }).catch(() => {}); // Non-critical - onboarding state is advisory
        
        onComplete();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save company information");
      }
    } catch (error) {
      toast.error("Failed to save company information");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
        data-testid="company-setup-modal"
      >
        <DialogHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center">
              <Buildings size={22} weight="bold" className="text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold">
                Company Information
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                Tell us about your business
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="py-6">
          <form id="company-setup-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Business Name */}
            <div className="space-y-2">
              <Label htmlFor="business_name" className="text-sm font-medium flex items-center gap-2">
                <Buildings size={16} className="text-zinc-500" />
                Business Name
              </Label>
              <Input
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                placeholder="ABC Plumbing Services"
                className={`h-11 border-2 ${errors.business_name ? "border-red-400" : "border-zinc-200"}`}
                data-testid="company-setup-name"
              />
              {errors.business_name && (
                <p className="text-xs text-red-500">{errors.business_name}</p>
              )}
              <p className="text-xs text-zinc-500">
                This appears on invoices and estimates
              </p>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-2">
                <Phone size={16} className="text-zinc-500" />
                Business Phone
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className={`h-11 border-2 ${errors.phone ? "border-red-400" : "border-zinc-200"}`}
                data-testid="company-setup-phone"
              />
              {errors.phone && (
                <p className="text-xs text-red-500">{errors.phone}</p>
              )}
              <p className="text-xs text-zinc-500">
                Customers can reach you at this number
              </p>
            </div>

            {/* Address Section */}
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                <MapPin size={16} className="text-zinc-500" />
                Business Address
              </div>

              {/* Street */}
              <div className="space-y-2">
                <Label htmlFor="address_street" className="text-sm">
                  Street Address
                </Label>
                <Input
                  id="address_street"
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  className={`h-11 border-2 ${errors.address_street ? "border-red-400" : "border-zinc-200"}`}
                  data-testid="company-setup-street"
                />
                {errors.address_street && (
                  <p className="text-xs text-red-500">{errors.address_street}</p>
                )}
              </div>

              {/* City, State, ZIP */}
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-3 space-y-2">
                  <Label htmlFor="address_city" className="text-sm">
                    City
                  </Label>
                  <Input
                    id="address_city"
                    name="address_city"
                    value={formData.address_city}
                    onChange={handleChange}
                    placeholder="Boston"
                    className={`h-11 border-2 ${errors.address_city ? "border-red-400" : "border-zinc-200"}`}
                    data-testid="company-setup-city"
                  />
                  {errors.address_city && (
                    <p className="text-xs text-red-500">{errors.address_city}</p>
                  )}
                </div>

                <div className="col-span-1 space-y-2">
                  <Label htmlFor="address_state" className="text-sm">
                    State
                  </Label>
                  <select
                    id="address_state"
                    name="address_state"
                    value={formData.address_state}
                    onChange={handleChange}
                    className={`h-11 w-full rounded-md border-2 bg-white px-2 text-sm ${
                      errors.address_state ? "border-red-400" : "border-zinc-200"
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    data-testid="company-setup-state"
                  >
                    <option value="">--</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.address_state && (
                    <p className="text-xs text-red-500">{errors.address_state}</p>
                  )}
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="address_zip" className="text-sm">
                    ZIP Code
                  </Label>
                  <Input
                    id="address_zip"
                    name="address_zip"
                    value={formData.address_zip}
                    onChange={handleChange}
                    placeholder="02101"
                    maxLength={10}
                    className={`h-11 border-2 ${errors.address_zip ? "border-red-400" : "border-zinc-200"}`}
                    data-testid="company-setup-zip"
                  />
                  {errors.address_zip && (
                    <p className="text-xs text-red-500">{errors.address_zip}</p>
                  )}
                </div>
              </div>
            </div>
          </form>
        </DialogBody>

        <DialogFooter className="pt-4 border-t border-zinc-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="border-2"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="company-setup-form"
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white min-w-[140px]"
            data-testid="company-setup-save"
          >
            {saving ? (
              <>
                <SpinnerGap size={18} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save & Continue"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
